/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { ICoreContext, Image } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { IContentBlockProps } from '../definition-extensions/content-block.ext.props.autogenerated';
import { IContentBlockData } from './content-block.data';
import { ICategoryHierarchyViewProps } from '../../../modules/dobbies-search-result-container/dobbies-search-result-container';
import { Link, Separator } from '../../../modules/dobbies-search-result-container/components';
import { CategoryHierarchy as CategoryHierarchyData } from '@msdyn365-commerce/commerce-entities';
const ContentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<IContentBlockData>> = props => {
    const {
        config: { isCategoryNameTitle, hideClubPlusBanner, clubPlusTierId },
        data: {
            modulesSharedState: { result: modulesSharedState }
        },
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        context,
        title
    } = props;

    const {
        request: {
            urlTokens: { pageType }
        }
    } = context;

    /* VSI Cutomization - START - 11/11/20 */
    const [isMobile, SetIsMobile] = useState(false);
    const [categoryName, SetCategoryName] = useState<string | undefined>();
    const [isClubPlus, SetIsClubPlus] = useState<boolean | undefined>(undefined);
    const [isImageAvailable, setIsImageAvailable] = useState<boolean | undefined>();

    const _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = props;
        const categoryHierarchy = data.categoryHierarchy.result;
        const categoryLinks =
            categoryHierarchy &&
            categoryHierarchy.map((value: CategoryHierarchyData, index: number) => {
                return <Link key={index} text={value.Name} ariaLabel={`"Go To: ${value.Name}`} href={value.Url} />;
            });
        const categoryLinkSeparator = <Separator separator='/' />;

        return {
            CategoryHierarchyContainer: { tag: 'nav', className: 'ms-search-result-container__category-hierarchy' },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    const renderCategoryHierarchy = (categoryHeirarchyData: ICategoryHierarchyViewProps): JSX.Element | null => {
        const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = categoryHeirarchyData;
        if (categoryHierarchyList) {
            return (
                <Node {...CategoryHierarchyContainer}>
                    {categoryHierarchyList.map((category, index) => (
                        <React.Fragment key={index}>
                            {category}
                            {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                        </React.Fragment>
                    ))}
                </Node>
            );
        }

        return null;
    };

    useEffect(() => {
        const _isMobile = isMobileView(context);
        SetIsMobile(_isMobile);

        // Check if pageType is Category and state is not set yet, get its category name
        if (categoryName === undefined && pageType && pageType.toLowerCase() === 'category') {
            if (isCategoryNameTitle) {
                SetCategoryName(modulesSharedState?.categoryName);
            }
            if (isImageAvailable === undefined) {
                const isImage = props.config.image ? true : false;
                setIsImageAvailable(isImage);
            }
        }
        // Check if clubplus banner is set to hide on my account page for club plus members and state is not set yet i.e. undefined
        if (hideClubPlusBanner && isClubPlus === undefined) {
            modulesSharedState?.customerLoyaltyTierId === clubPlusTierId?.toLowerCase() ? SetIsClubPlus(true) : SetIsClubPlus(false);
        }
    });

    // Dont show banner for club plus members on my account or if tier id is not set yet
    if (hideClubPlusBanner && (isClubPlus === undefined || isClubPlus === true)) {
        return <></>;
    }

    const mobileImage = _renderImageForMobile(props);

    const categoryHeading = title;

    // const doNoImageMagic = isImageAvailable === false && !isMobile;

    // const checkMobileImageAvailable = isImageAvailable === false && isMobile === true;
    // const detailsStyles = checkMobileImageAvailable ? {display : 'none'}: {}
    // const detailsStyles = doNoImageMagic ? {} : { display: 'none' };

    /* VSI Cutomization - END */
    const checkClassName = () => {
        if (isImageAvailable === true && isMobile === false) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === true && isMobile === true) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === false && isMobile === false) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === false && isMobile === true) {
            return classnames(contentBlockContainer.className, '');
        } else {
            return classnames(contentBlockContainer.className, '');
        }
    };
    let hasSearchResultsContainer;
    if (MsDyn365.isBrowser && window) {
        const hasContentBlockImage = !document.getElementsByClassName('no-image-categories')?.length;
        hasSearchResultsContainer =
            document.getElementsByClassName('ms-search-result-container__product-section') &&
            document.getElementsByClassName('ms-search-result-container__product-section').length &&
            hasContentBlockImage;
        if (!hasSearchResultsContainer) {
            hasSearchResultsContainer =
                document.getElementsByClassName('ms-search-result-container__Products') &&
                document.getElementsByClassName('ms-search-result-container__Products').length &&
                hasContentBlockImage;
        }
    }

    const categoryHeirarchy = _getCategoryHierarchy();
    if (imageLink) {
        return (
            <>
                {hasSearchResultsContainer ? <div>{renderCategoryHierarchy(categoryHeirarchy)}</div> : <></>}
                <Module {...contentBlockContainer} className={checkClassName()}>
                    <Node
                        {...contentBlockAnchorTag}
                        href={imageLink}
                        className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                        aria-label={imageAriaLabel}
                    >
                        {/* VSI Cutomization - START - 11/11/20 */}
                        <Node {...imageContainer}>{isMobile === true && mobileImage ? mobileImage : image}</Node>
                        {/* VSI Cutomization - END */}
                    </Node>
                    <Node {...detailsContainer}>
                        {/* {!(hasSearchResultsContainer && hasSearchResultsContainer.length) ? categoryHeading : <></>} */}
                        {categoryHeading}
                        {text}
                        {links}
                    </Node>
                </Module>
            </>
        );
    } else {
        return (
            <>
                {hasSearchResultsContainer ? <div>{renderCategoryHierarchy(categoryHeirarchy)}</div> : <></>}
                <Module {...contentBlockContainer} className={checkClassName()}>
                    {/* VSI Cutomization - START - 11/11/20 */}
                    <Node {...imageContainer}>{isMobile === true && mobileImage ? mobileImage : image}</Node>
                    {/* VSI Cutomization - END */}
                    <Node {...detailsContainer}>
                        {/* {!(hasSearchResultsContainer && hasSearchResultsContainer.length) ? categoryHeading : <></>} */}
                        {categoryHeading}
                        {text}
                        {links}
                    </Node>
                </Module>
            </>
        );
    }
};

/* VSI Cutomization - START - 11/11/20 */

/* Following function renders if mobile view has different image than that of desktop image.
This function is specifically written for sale banner on homepage but can be used anywhere user want to add separate image for mobile. */
const _renderImageForMobile = (props: IContentBlockViewProps & IContentBlockProps<{}>): JSX.Element | undefined => {
    const {
        config: { imageForMobile },
        context: { request }
    } = props;
    if (imageForMobile === undefined) {
        return undefined;
    }

    return (
        <Image
            className={'promotion-mobile-image'}
            {...imageForMobile}
            gridSettings={request.gridSettings!}
            imageSettings={imageForMobile.imageSettings}
            loadFailureBehavior='default'
            role='tabpanel'
            id={`${props.id}__static-image`}
        />
    );
};

/* Following function checks device type and handles state accordingly if view changes, state will be updated and view will be shown according to device. */
const isMobileView = (context: ICoreContext): boolean => {
    if (typeof window !== undefined && window.innerWidth) {
        const gridSettings = context.request.gridSettings;

        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth < gridSettings.xs.w) {
                return true;
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                return false;
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                return false;
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return false;
            } else {
                return false;
            }
        }
    }

    return false;
};

/* VSI Cutomization - END */

export default ContentBlockView;
